import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { ArrowRight, FileText } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { formatCurrency, formatDate } from '@/lib/utils'

export const externalPrescriptionStatuses = [
  { value: 'creata', label: 'Creata' },
  { value: 'in_lavorazione', label: 'In lavorazione' },
  { value: 'completata', label: 'Completata' },
  { value: 'annullata', label: 'Annullata' },
] as const
export type ExternalPrescriptionStatus = typeof externalPrescriptionStatuses[number]['value']

export function ExternalPrescriptionStatusBadge({ status }: { status: ExternalPrescriptionStatus }) {
  const styles: Record<ExternalPrescriptionStatus, string> = {
    creata: 'border-gray-200 bg-gray-100 text-gray-700',
    in_lavorazione: 'border-blue-200 bg-blue-50 text-blue-800',
    completata: 'border-green-200 bg-green-50 text-green-800',
    annullata: 'border-gray-200 bg-gray-100 text-gray-600',
  }
  return <Badge variant="outline" className={styles[status]}>{externalPrescriptionStatuses.find((item) => item.value === status)?.label ?? status}</Badge>
}

export function ExternalPrescriptionList() {
  const prefix = useId()
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canQuery = Boolean(profile?.company_id && hasPermission('view_external_prescriptions'))
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [laboratoryId, setLaboratoryId] = useState('')
  const [status, setStatus] = useState<ExternalPrescriptionStatus | ''>('')
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [searchInput])
  const laboratories = useQuery(api.externalLabs.listPrescriptionLaboratories, canQuery ? {} : 'skip')
  const prescriptions = useQuery(api.externalLabs.listPrescriptions, canQuery ? {
    laboratory_id: laboratoryId ? laboratoryId as Id<'external_laboratories'> : undefined,
    status: status || undefined,
    search: search || undefined,
  } : 'skip')
  const filtered = Boolean(searchInput || laboratoryId || status)
  const canViewPrices = hasPermission('view_external_prices')
  const selectClass = 'h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
  const clearFilters = () => { setSearchInput(''); setSearch(''); setLaboratoryId(''); setStatus('') }

  if (!canQuery) return <p className="rounded-lg border bg-gray-50 p-5 text-sm text-gray-700">Seleziona una clinica attiva per consultare le prescrizioni esterne.</p>

  return <div className="space-y-5">
    <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <label htmlFor={`${prefix}-search`} className="mb-1.5 block text-sm font-medium text-gray-800">Cerca prescrizione</label>
        <Input id={`${prefix}-search`} type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Paziente, medico o lavoro" maxLength={200} />
      </div>
      <div>
        <label htmlFor={`${prefix}-lab`} className="mb-1.5 block text-sm font-medium text-gray-800">Laboratorio</label>
        <select id={`${prefix}-lab`} className={selectClass} value={laboratoryId} onChange={(event) => setLaboratoryId(event.target.value)}>
          <option value="">Tutti i laboratori esterni</option>
          {laboratories?.map((lab) => <option key={lab._id} value={lab._id}>{lab.name}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor={`${prefix}-status`} className="mb-1.5 block text-sm font-medium text-gray-800">Stato</label>
        <select id={`${prefix}-status`} className={selectClass} value={status} onChange={(event) => setStatus(event.target.value as ExternalPrescriptionStatus | '')}>
          <option value="">Tutti gli stati</option>
          {externalPrescriptionStatuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </div>
    </div>
    {prescriptions === undefined || search !== searchInput.trim() ? <Loading text="Caricamento prescrizioni esterne…" /> : prescriptions.length === 0 ? <div className="rounded-lg border border-dashed p-8 text-center">
      <FileText className="mx-auto h-7 w-7 text-gray-500" aria-hidden="true" />
      <h3 className="mt-3 font-semibold text-gray-900">{filtered ? 'Nessuna prescrizione con questi filtri' : 'Nessuna prescrizione esterna'}</h3>
      <p className="mx-auto mt-2 max-w-prose text-sm text-gray-600">{filtered ? 'Prova a cercare un altro paziente o modifica i filtri.' : 'Crea una prescrizione e scegli il laboratorio a cui affidare il lavoro.'}</p>
      {filtered ? <Button className="mt-4" variant="outline" onClick={clearFilters}>Rimuovi filtri</Button> : hasPermission('create_external_prescription') ? <Button className="mt-4" variant="outline" asChild><Link to="/prescriptions/new">Nuova prescrizione</Link></Button> : null}
    </div> : <div>
      <p className="mb-3 text-sm text-gray-600" role="status">{prescriptions.length} {prescriptions.length === 1 ? 'prescrizione' : 'prescrizioni'}</p>
      <ul className="divide-y rounded-lg border bg-white">
        {prescriptions.map((prescription) => <li key={prescription._id}>
          <Link to={`/prescriptions/external/${prescription._id}`} className="flex min-w-0 flex-col gap-3 p-4 transition-colors hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2"><span className="break-words font-semibold text-gray-900">{prescription.patient_name}</span><ExternalPrescriptionStatusBadge status={prescription.status} /></div>
              <p className="mt-1 break-words text-sm text-gray-700">{prescription.laboratory_name} · {prescription.doctor_name}</p>
              <p className="mt-1 line-clamp-2 break-words text-sm text-gray-600">{prescription.work_description}</p>
              <p className="mt-2 text-xs text-gray-600">{prescription.number} · Creata il {formatDate(prescription.created_at)}{prescription.due_date ? ` · Consegna ${formatDate(new Date(`${prescription.due_date}T12:00:00`).getTime())}` : ''}</p>
            </div>
            <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
              {canViewPrices && prescription.total_cost !== undefined && <span className="font-medium tabular-nums text-gray-900">{formatCurrency(prescription.total_cost)}</span>}
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700">Apri<span className="sr-only"> prescrizione di {prescription.patient_name}</span><ArrowRight className="h-4 w-4" aria-hidden="true" /></span>
            </div>
          </Link>
        </li>)}
      </ul>
    </div>}
  </div>
}
